import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

import {
  MAX_SUBJECT_LEN,
  type EmailSchedule,
  type ScheduleKind,
  type ScheduleType,
  WINDOW_END_MIN,
  WINDOW_START_MIN,
  clockedSlots,
  formatMinutes,
  frequencyChoices,
} from './scheduleTypes';
import { ThemedSelect, type ThemedSelectOption } from '../../../components/common';
import styles from './ScheduleEmailModal.module.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_FREQUENCY = 30;
const DEFAULT_CLOCKED_FIRST = WINDOW_START_MIN;

export interface ScheduleEmailSubmitArgs {
  subject: string;
  to: string;
  schedule: ScheduleKind;
}

export interface ScheduleEmailModalProps {
  open: boolean;
  defaultSubject: string;
  defaultTo: string;
  existing: EmailSchedule | null;
  isLoadingExisting: boolean;
  onCancel: () => void;
  onSubmit: (args: ScheduleEmailSubmitArgs) => Promise<void> | void;
}

const pickInitialType = (existing: EmailSchedule | null): ScheduleType =>
  existing?.schedule.kind === 'clocked' ? 'clocked' : 'frequency';

const pickInitialClocked = (existing: EmailSchedule | null): number[] =>
  existing?.schedule.kind === 'clocked'
    ? [...existing.schedule.times_minutes]
    : [DEFAULT_CLOCKED_FIRST];

const pickInitialFrequency = (existing: EmailSchedule | null): number =>
  existing?.schedule.kind === 'frequency'
    ? existing.schedule.every_minutes
    : DEFAULT_FREQUENCY;

const nextFreeSlot = (used: number[]): number | null => {
  const slots = clockedSlots();
  for (const slot of slots) {
    if (!used.includes(slot)) return slot;
  }
  return null;
};

export const ScheduleEmailModal: React.FC<ScheduleEmailModalProps> = ({
  open,
  defaultSubject,
  defaultTo,
  existing,
  isLoadingExisting,
  onCancel,
  onSubmit,
}) => {
  const [subject, setSubject] = useState<string>(defaultSubject);
  const [to, setTo] = useState<string>(defaultTo);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('frequency');
  const [clockedTimes, setClockedTimes] = useState<number[]>([DEFAULT_CLOCKED_FIRST]);
  const [frequency, setFrequency] = useState<number>(DEFAULT_FREQUENCY);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const subjectRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setSubject(existing?.subject ?? defaultSubject);
    setTo(existing?.recipient ?? defaultTo);
    setScheduleType(pickInitialType(existing));
    setClockedTimes(pickInitialClocked(existing));
    setFrequency(pickInitialFrequency(existing));
    setSubmitError(null);
    setShowErrors(false);
    setSubmitting(false);
    const id = window.setTimeout(() => {
      subjectRef.current?.focus();
      subjectRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, existing, defaultSubject, defaultTo]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  const slots = useMemo(() => clockedSlots(), []);
  const slotOptions = useMemo<ThemedSelectOption<number>[]>(
    () => slots.map((slot) => ({ value: slot, label: formatMinutes(slot) })),
    [slots],
  );
  const freqOptions = useMemo<ThemedSelectOption<number>[]>(
    () => frequencyChoices().map((m) => ({ value: m, label: `${m} minutes` })),
    [],
  );

  if (!open) return null;

  const trimmedSubject = subject.trim();
  const trimmedTo = to.trim();

  const subjectError =
    trimmedSubject.length === 0
      ? 'Subject is required.'
      : trimmedSubject.length > MAX_SUBJECT_LEN
        ? `Subject must be at most ${MAX_SUBJECT_LEN} characters.`
        : null;
  const toError =
    trimmedTo.length === 0
      ? 'Recipient email is required.'
      : !EMAIL_REGEX.test(trimmedTo)
        ? 'Recipient email is not valid.'
        : null;

  const clockedError =
    scheduleType === 'clocked' && clockedTimes.length === 0
      ? 'Add at least one time.'
      : null;

  const hasErrors = subjectError !== null || toError !== null || clockedError !== null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const schedule: ScheduleKind =
        scheduleType === 'clocked'
          ? { kind: 'clocked', times_minutes: [...clockedTimes].sort((a, b) => a - b) }
          : { kind: 'frequency', every_minutes: frequency };
      await onSubmit({
        subject: trimmedSubject,
        to: trimmedTo,
        schedule,
      });
    } catch (caught: unknown) {
      const message = caught instanceof Error ? caught.message : 'Failed to save schedule.';
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onCancel();
  };

  const updateClockedAt = (idx: number, value: number) => {
    setClockedTimes((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const removeClockedAt = (idx: number) => {
    setClockedTimes((prev) => prev.filter((_, i) => i !== idx));
  };

  const addClockedSlot = () => {
    setClockedTimes((prev) => {
      const free = nextFreeSlot(prev);
      return free === null ? prev : [...prev, free];
    });
  };

  const submitLabel = existing ? 'Overwrite & Save' : 'Save Schedule';

  return (
    <div
      className={styles.backdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="schedule-email-modal"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-email-modal-title"
      >
        <form onSubmit={handleSubmit} noValidate>
          <h2 id="schedule-email-modal-title" className={styles.title}>
            Schedule dashboard email
          </h2>

          {existing ? (
            <div className={styles.warning} data-testid="schedule-email-warning">
              This dashboard already has a schedule. Submitting will overwrite it.
            </div>
          ) : null}

          {isLoadingExisting ? (
            <div className={styles.warning} data-testid="schedule-email-loading">
              Checking for an existing schedule…
            </div>
          ) : null}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="schedule-email-subject">
              Subject
            </label>
            <input
              id="schedule-email-subject"
              ref={subjectRef}
              className={`${styles.input} ${showErrors && subjectError ? styles.inputInvalid : ''}`}
              type="text"
              value={subject}
              maxLength={MAX_SUBJECT_LEN + 1}
              onChange={(event) => setSubject(event.target.value)}
              autoComplete="off"
              data-testid="schedule-email-subject"
            />
            {showErrors && subjectError ? (
              <span className={styles.fieldError} data-testid="schedule-email-subject-error">
                {subjectError}
              </span>
            ) : null}
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="schedule-email-to">
              To
            </label>
            <input
              id="schedule-email-to"
              className={`${styles.input} ${showErrors && toError ? styles.inputInvalid : ''}`}
              type="email"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              autoComplete="email"
              data-testid="schedule-email-to"
            />
            {showErrors && toError ? (
              <span className={styles.fieldError} data-testid="schedule-email-to-error">
                {toError}
              </span>
            ) : null}
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Schedule type</span>
            <div className={styles.radioGroup}>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="schedule-type"
                  value="clocked"
                  checked={scheduleType === 'clocked'}
                  onChange={() => setScheduleType('clocked')}
                  data-testid="schedule-type-clocked"
                />
                Clocked
              </label>
              <label className={styles.radioOption}>
                <input
                  type="radio"
                  name="schedule-type"
                  value="frequency"
                  checked={scheduleType === 'frequency'}
                  onChange={() => setScheduleType('frequency')}
                  data-testid="schedule-type-frequency"
                />
                Frequency
              </label>
            </div>
          </div>

          {scheduleType === 'clocked' ? (
            <div className={styles.panel} data-testid="schedule-panel-clocked">
              <p className={styles.panelHint}>
                Send at each of the listed times (America/New_York).
              </p>
              <div className={styles.timesList}>
                {clockedTimes.map((value, idx) => (
                  <div key={`${idx}-${value}`} className={styles.timeRow}>
                    <ThemedSelect<number>
                      className={styles.timeSelect}
                      value={value}
                      options={slotOptions}
                      onChange={(next) => updateClockedAt(idx, next)}
                      ariaLabel={`Clocked time ${idx + 1}`}
                      testId={`schedule-clocked-select-${idx}`}
                    />
                    {clockedTimes.length > 1 ? (
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeClockedAt(idx)}
                        aria-label={`Remove time ${formatMinutes(value)}`}
                        data-testid={`schedule-clocked-remove-${idx}`}
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className={styles.addButton}
                onClick={addClockedSlot}
                disabled={clockedTimes.length >= slots.length}
                data-testid="schedule-clocked-add"
              >
                Add time
              </button>
              {showErrors && clockedError ? (
                <span className={styles.fieldError} data-testid="schedule-clocked-error">
                  {clockedError}
                </span>
              ) : null}
            </div>
          ) : (
            <div className={styles.panel} data-testid="schedule-panel-frequency">
              <p className={styles.panelHint}>
                Send every N minutes between {formatMinutes(WINDOW_START_MIN)} and{' '}
                {formatMinutes(WINDOW_END_MIN)} (America/New_York).
              </p>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="schedule-frequency-select">
                  Every
                </label>
                <ThemedSelect<number>
                  id="schedule-frequency-select"
                  value={frequency}
                  options={freqOptions}
                  onChange={setFrequency}
                  testId="schedule-frequency-select"
                />
              </div>
            </div>
          )}

          {submitError ? (
            <p className={styles.formError} data-testid="schedule-email-error">
              {submitError}
            </p>
          ) : null}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onCancel}
              disabled={submitting}
              data-testid="schedule-email-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={submitting}
              data-testid="schedule-email-confirm"
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
